import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  Button,
  useTheme,
} from '@mui/material';
import { 
  ArrowBack,
  Work, 
  Person,
  Refresh,
  FileDownload,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store/types';
import { 
  fetchApplicationsByFeedId, 
  setFeedDetails, 
  clearApplicationsError,
  updateApplicationStatus
} from '@/store';
import { ApplicationsTable } from '@/components/common';
import { applicationsService } from '@/services/applications/applicationsService';

export const ApplicationsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { feedId } = useParams<{ feedId: string }>();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  
  const { 
    applications, 
    pagination, 
    isLoading, 
    error, 
    feedDetails 
  } = useSelector((state: RootState) => state.applications);
// console.log(applications[0].userId.jobPreferences?.jobTitle)
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Get feed details from URL params
  const feedTitle = searchParams.get('title');
  const feedType = searchParams.get('type');
  const applicationCount = searchParams.get('count');

  useEffect(() => {
    if (feedTitle || feedType || applicationCount) {
      dispatch(setFeedDetails({
        title: feedTitle || undefined,
        type: feedType || undefined,
        applicationCount: applicationCount ? parseInt(applicationCount) : undefined,
      }));
    }
  }, [dispatch, feedTitle, feedType, applicationCount]);

  useEffect(() => {
    if (feedId) {
      fetchApplications();
    }
  }, [feedId, paginationState.pageIndex, paginationState.pageSize]);

  const fetchApplications = () => {
    if (!feedId) return;

    dispatch(fetchApplicationsByFeedId({
      feedId,
      params: {
        page: paginationState.pageIndex + 1,
        limit: paginationState.pageSize,
        sort: '-appliedAt',
      },
    }));
  };

  const handleRetry = () => {
    dispatch(clearApplicationsError());
    fetchApplications();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleExportCSV = async () => {
    if (!feedId) return;
    
    try {
      const blob = await applicationsService.exportApplicationsToCSV(feedId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `applications-${feedId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  const handleStatusUpdate = (applicationId: string, newStatus: string) => {
    dispatch(updateApplicationStatus({ applicationId, status: newStatus }));
  };
console.log(applications)
  const getPageTitle = () => {
    const typeIcon = feedDetails?.type === 'job' ? 
      <Work sx={{ mr: 1, fontSize: 28 }} /> : 
      <Person sx={{ mr: 1, fontSize: 28 }} />;
    
    const typeText = feedDetails?.type === 'job' ? 'Job Applications' : 'Recruiter Profile Views';
    const title = applications&&applications.length > 0 &&applications[0].userId.jobPreferences?.jobTitle
    const count = feedDetails?.applicationCount ?? applications.length;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {typeIcon}
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            {typeText}
          </Typography>
           <Typography variant="h5" component="h5" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Chip
              label={`${count} ${count === 1 ? 'application' : 'applications'}`}
              color="primary"
              variant="outlined"
              size="small"
            />
            {feedDetails?.type && (
              <Chip
                label={feedDetails.type === 'job' ? 'Job Post' : 'Job Seeker Profile'}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  if (!feedId) {
    return (
      <Box sx={{ maxWidth: '100%', mx: 'auto', p: 3 }}>
        <Alert severity="error">
          Invalid feed ID. Please check the URL and try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={handleGoBack}
          sx={{ 
            mb: 2,
            textTransform: 'none',
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'action.hover',
              color: 'primary.main',
            },
          }}
        >
          Back
        </Button>

        {/* Breadcrumbs */}
        {/* <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            href="/talky" 
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Dashboard
          </Link>
          <Typography color="text.primary">
            {feedDetails?.type === 'job' ? 'Job Applications' : 'Profile Views'}
          </Typography>
        </Breadcrumbs> */}

        {/* Page Title */}
        {getPageTitle()}

        {/* Feed Title */}
        {feedDetails?.title && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              &ldquo;{feedDetails.title}&rdquo;
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Export Button */}
      {applications.length > 0 && !isLoading && !error && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            startIcon={<FileDownload />}
            variant="outlined"
            onClick={handleExportCSV}
            size="small"
            sx={{
              textTransform: 'none',
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.lighter',
                borderColor: 'primary.main',
              },
            }}
          >
            Export CSV
          </Button>
        </Box>
      )}

      {/* Content Section */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button 
              variant="outlined" 
              onClick={handleRetry}
              startIcon={<Refresh />}
            >
              Try Again
            </Button>
          </Box>
        ) : applications.length === 0 && !isLoading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 8,
            px: 3,
          }}>
            {feedDetails?.type === 'job' ? 
              <Work sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} /> : 
              <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            }
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No {feedDetails?.type === 'job' ? 'applications' : 'views'} yet
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {feedDetails?.type === 'job' ? 
                'When people apply for this job, they will appear here.' :
                'When people view this profile, they will appear here.'
              }
            </Typography>
          </Box>
        ) : (
          <ApplicationsTable
            applications={applications}
            feedId={feedId}
            loading={isLoading}
            totalCount={pagination?.total}
            pagination={paginationState}
            onPaginationChange={setPaginationState}
            enablePagination={true}
            enableSelection={false}
            onStatusUpdate={handleStatusUpdate}
            customTableOptions={{
              muiTableHeadCellProps: {
                sx: {
                  fontWeight: 600,
                  backgroundColor: theme.palette.grey[50],
                  borderBottom: `2px solid ${theme.palette.divider}`,
                },
              },
            }}
          />
        )}
      </Paper>
    </Box>
  );
};